import React, { useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: number | string;
  label: string;
  subLabel?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: number | string | '';
  onChange: (value: number | string | '') => void;
  placeholder?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
  filterFn?: (option: SearchableSelectOption, search: string) => boolean;
  'aria-label'?: string;
  className?: string;
}

function normalizeSearchText(value: string): string {
  return value
    .replace(/ك/g, 'ک')
    .replace(/ي/g, 'ی')
    .replace(/ة/g, 'ه')
    .replace(/[أإٱآ]/g, 'ا')
    .replace(/[ؤ]/g, 'و')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[\u200c\u200f\u200e]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const defaultFilter = (opt: SearchableSelectOption, q: string) => {
  const normalizedQuery = normalizeSearchText(q);
  if (!normalizedQuery) return true;
  return (
    normalizeSearchText(opt.label).includes(normalizedQuery) ||
    (opt.subLabel ? normalizeSearchText(opt.subLabel).includes(normalizedQuery) : false)
  );
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'جستجو یا انتخاب...',
  disabled = false,
  searchPlaceholder = 'جستجو...',
  filterFn = defaultFilter,
  'aria-label': ariaLabel,
  className = '',
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const filtered = useMemo(() => options.filter((o) => filterFn(o, search)), [options, search, filterFn]);

  useEffect(() => {
    if (open) {
      setHighlight(0);
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open, search, filtered.length]);

  const displayText = selected ? selected.label : search || '';

  const handleSelect = (opt: SearchableSelectOption) => {
    onChange(opt.value);
    setSearch('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSearch('');
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && filtered[highlight]) {
      e.preventDefault();
      handleSelect(filtered[highlight]);
    }
  };

  return (
    <div className={`relative ${className}`} dir="rtl">
      <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" size={18} />
      <input
        ref={inputRef}
        type="text"
        value={open ? search : displayText}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        autoComplete="off"
        className="w-full pr-10 pl-10 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-right disabled:bg-slate-100 disabled:cursor-not-allowed"
      />
      {value !== '' && value !== undefined && !disabled ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
          aria-label="پاک کردن"
        >
          <X size={18} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 disabled:opacity-50"
        >
          <ChevronDown size={20} className={open ? 'rotate-180' : ''} />
        </button>
      )}

      {open && (
        <div
          ref={listRef}
          className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
        >
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              {options.length === 0 ? 'موردی وجود ندارد.' : 'نتیجه‌ای یافت نشد.'}
            </div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt)}
                className={`w-full text-right px-4 py-3 hover:bg-teal-50 flex flex-col gap-0.5 border-b border-slate-100 last:border-0 transition-colors ${
                  value === opt.value ? 'bg-teal-50' : ''
                } ${highlight === idx ? 'bg-teal-50' : ''}`}
              >
                <span className="font-medium text-slate-800">{opt.label}</span>
                {opt.subLabel && <span className="text-xs text-slate-500">{opt.subLabel}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
